import { useState, useEffect, useCallback } from 'react'
import {
  UserPOIComment,
  CreateUserPOICommentData,
  UpdateUserPOICommentData
} from '../types/userPoiComments'
import {
  getUserPOIComments,
  createUserPOIComment,
  updateUserPOIComment,
  deleteUserPOIComment,
  subscribeToUserPOIComments
} from '../services/firebase/userPoiComments'

interface UseUserPOICommentsProps {
  poiId: string | null
  userId?: string
  userDisplayName?: string
  userPhotoURL?: string
}

export function useUserPOIComments({
  poiId,
  userId,
  userDisplayName,
  userPhotoURL
}: UseUserPOICommentsProps) {
  const [comments, setComments] = useState<UserPOIComment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Subscribe to comments when POI ID changes
  useEffect(() => {
    if (!poiId) {
      setComments([])
      return
    }

    setLoading(true)
    setError(null)

    // Subscribe to real-time updates
    const unsubscribe = subscribeToUserPOIComments(poiId, (updatedComments) => {
      setComments(updatedComments)
      setLoading(false)
    })

    // Cleanup subscription
    return () => {
      unsubscribe()
    }
  }, [poiId])

  // Add comment
  const addComment = useCallback(async (text: string): Promise<void> => {
    if (!poiId || !userId || !userDisplayName) {
      throw new Error('User must be authenticated to add comments')
    }

    if (!text.trim()) {
      throw new Error('Comment cannot be empty')
    }

    try {
      const commentData: CreateUserPOICommentData = {
        poiId,
        text: text.trim()
      }

      await createUserPOIComment(
        commentData,
        userId,
        userDisplayName,
        userPhotoURL
      )
    } catch (err) {
      console.error('Error adding comment:', err)
      throw new Error('Failed to add comment')
    }
  }, [poiId, userId, userDisplayName, userPhotoURL])

  // Update comment
  const editComment = useCallback(async (commentId: string, text: string): Promise<void> => {
    if (!text.trim()) {
      throw new Error('Comment cannot be empty')
    }

    try {
      const updates: UpdateUserPOICommentData = {
        text: text.trim()
      }

      await updateUserPOIComment(commentId, updates)
    } catch (err) {
      console.error('Error updating comment:', err)
      throw new Error('Failed to update comment')
    }
  }, [])

  // Delete comment
  const removeComment = useCallback(async (commentId: string): Promise<void> => {
    try {
      await deleteUserPOIComment(commentId)
    } catch (err) {
      console.error('Error deleting comment:', err)
      throw new Error('Failed to delete comment')
    }
  }, [])

  return {
    comments,
    loading,
    error,
    addComment,
    editComment,
    removeComment
  }
}